import { useAutoneTranslation } from '@autone/translations';
import { type SelectedGeneralFilters } from '@autone/ui';
import { useParams } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

import { type APP_NAME } from '../../consts';
import { type TableNamesWithAnyFilters } from '../../features/assortment-filters/types';
import { useGetAssortmentProductFiltersQuery } from '../../redux/assortment';
import { useAssortmentSessionStorageAtom } from '../../storage/session-storage';

import { type AssortmentGeneralFilterTranslatedOptions } from './useAssortmentGeneralFilters.types';
import { getFilterIdFromReactKey } from './utilities/react-keys';
import { transformApiFiltersToGeneralFilterGroups } from './utilities/transforms';

/**
 * Retrieves all available filters for the query result set given the
 * currently applied filters.
 *
 * @param sessionStorageKey - The session storage filter key to synchronise
 * fetched fiters with.
 *
 * ### Returns
 *
 * `translatedGeneralFilterOptions` - All valid filter options for the current query
 * already formatted and translated into `GeneralFilterGroup[]` to be used
 * with the `GeneralFilters` component.
 *
 * `generalFilterOptions` - All valid filter options for the
 * current query in the original format returned by the API.
 */
export const useAssortmentGeneralFilters = <T extends TableNamesWithAnyFilters>(
  tableName: T,
) => {
  const sessionStorageKey = `${tableName}.generalFilters` as const;

  const { common } = useAutoneTranslation<typeof APP_NAME>();

  // Get selected filters from session storage and current `product_id`
  const { productId } = useParams({ strict: false });

  const [
    selectedGeneralFiltersWithReactKey,
    setSelectedGeneralFiltersWithReactKey,
  ] = useAssortmentSessionStorageAtom(sessionStorageKey);

  type SessionStorageStateType = typeof selectedGeneralFiltersWithReactKey;

  /** converted to shape expected by APIs */
  const selectedGeneralFiltersApi = useMemo(() => {
    const res = Object.fromEntries(
      Object.entries(selectedGeneralFiltersWithReactKey).map(
        ([filterGroupId, filterGroup]) => [
          filterGroupId,
          filterGroup?.map(({ id, description }) => ({
            id: getFilterIdFromReactKey(id ?? ''),
            description,
          })) ?? [],
        ],
      ),
    ) as SessionStorageStateType;

    /**
     * Handle special cases where the API expects an array of IDs.
     */
    const productGroups =
      'product_groups' in res ? res.product_groups?.map(({ id }) => id!) : null;
    const locationClusters =
      'location_clusters' in res
        ? res.location_clusters?.map(({ id }) => id!)
        : null;
    const sizes = 'sizes' in res ? res.sizes?.map(({ id }) => id!) : null;

    const filtersAPIFormat = {
      ...res,
      product_groups: productGroups,
      location_clusters: locationClusters,
      sizes,
    };

    /**
     * Typecast required because this style of transformations break generic type inference
     * across endpoints. Some endpoints might not accept all available filter dimensions that
     * require these transformations.
     *
     * However, the fact that they're always added to the `filtersAPIFormat` object even if
     * the runtime guarantees that they'll always be `null` for certain endpoints means that
     * they will permanently stay as part of the inferred `selectedGeneralFiltersApi` type.
     * Thus, some endpoints will complain if they don't accept these filter dimensions at all
     * or expect them to be exclusively `null | undefined`.
     */
    type FiltersAPIFormatType = typeof filtersAPIFormat;
    type SelectedFilterDimensions = keyof SessionStorageStateType;
    type SelectedFiltersAPIFormatType = Pick<
      FiltersAPIFormatType,
      SelectedFilterDimensions
    >;

    return filtersAPIFormat as SelectedFiltersAPIFormatType;
  }, [selectedGeneralFiltersWithReactKey]);

  const queryArgs = useMemo(() => {
    return {
      body: {
        filters: {
          ...selectedGeneralFiltersApi,
          product_ids: productId ? [productId] : null,
        },
      },
    };
  }, [selectedGeneralFiltersApi, productId]);

  const { data, ...result } = useGetAssortmentProductFiltersQuery(queryArgs);

  // convert valid filter options from API to the shape expected by GeneralFilters
  const translatedGeneralFilterOptions = useMemo(() => {
    if (!data)
      return { product: [], geographic: [], location_clusters: [], sku: [] };

    const {
      filters: { geographic, product, location_cluster, sku },
    } = data;

    return {
      product: product
        ? transformApiFiltersToGeneralFilterGroups(product, common)
        : [],
      geographic: geographic
        ? transformApiFiltersToGeneralFilterGroups(geographic, common)
        : [],
      location_clusters: location_cluster
        ? transformApiFiltersToGeneralFilterGroups(location_cluster, common)
        : [],
      sku: sku ? transformApiFiltersToGeneralFilterGroups(sku, common) : [],
    } satisfies AssortmentGeneralFilterTranslatedOptions;
  }, [data, common]);

  /**
   * General filters state converted to `SelectedGeneralFilters` type for
   * use with components.
   */
  const selectedGeneralFilters = useMemo(() => {
    const result: SelectedGeneralFilters = {};
    for (const [filterGroupId, filterGroup] of Object.entries(
      selectedGeneralFiltersWithReactKey,
    )) {
      if (!filterGroup) continue;
      result[filterGroupId] = Object.fromEntries(
        filterGroup?.map(({ id }) => [id as string, true]) ?? [],
      );
    }
    return result;
  }, [selectedGeneralFiltersWithReactKey]);

  /**
   * TODO - The `GeneralFilters` component does not currently expose a generic
   * component signature. This means it's not possible to strongly type `selectedFilters`
   * at call sites. The typecasting in this callback is a workaround as we do not
   * have enough type information from `selectedFilters` to do proper type narrowing
   * for the `newSelectedAssortmentGeneralFilters` accumulator.
   *
   * State setter accepting the component `SelectedGeneralFilters` type
   * and converting it to the `sessionStorage` shape.
   */
  const setSelectedGeneralFilters = useCallback(
    (selectedFilters: SelectedGeneralFilters) => {
      const newSelectedAssortmentGeneralFilters = {} as Record<
        keyof SessionStorageStateType,
        { id: string; description: string }[]
      >;

      for (const filterGroup of [
        ...translatedGeneralFilterOptions.product,
        ...translatedGeneralFilterOptions.geographic,
        ...translatedGeneralFilterOptions.location_clusters,
        ...translatedGeneralFilterOptions.sku,
      ]) {
        const { options } = filterGroup;
        const selectedOptions = options.filter(
          (option) => selectedFilters[filterGroup.id]?.[option.id],
        );
        if (selectedOptions.length > 0) {
          newSelectedAssortmentGeneralFilters[
            filterGroup.id as keyof SessionStorageStateType
          ] = selectedOptions.map(({ id, translatedDisplayName }) => ({
            id,
            description: translatedDisplayName,
          }));
        }
      }

      setSelectedGeneralFiltersWithReactKey(
        newSelectedAssortmentGeneralFilters as SessionStorageStateType,
      );
    },
    [translatedGeneralFilterOptions, setSelectedGeneralFiltersWithReactKey],
  );

  // selected counts
  const selectedGeneralFilterGroupsArray = Object.values(
    selectedGeneralFilters,
  );

  const selectedGeneralFilterGroupsCount =
    selectedGeneralFilterGroupsArray.length;

  const selectedGeneralFiltersCount = selectedGeneralFilterGroupsArray.reduce(
    (sum, filterGroup) => {
      return (
        sum +
        Object.entries(filterGroup).filter(([_, selected]) => selected).length
      );
    },
    0,
  );

  return useMemo(() => {
    if (selectedGeneralFiltersWithReactKey === undefined) {
      return {
        dynamicFiltersQueryResult: null,
        setSelectedGeneralFilters,
        selectedGeneralFilters,
        selectedGeneralFiltersApi,
        selectedGeneralFilterGroupsCount,
        selectedGeneralFiltersCount,
        translatedGeneralFilterOptions,
        generalFilterOptions: data?.filters ?? null,
      };
    }
    return {
      dynamicFiltersQueryResult: result,
      setSelectedGeneralFilters,
      selectedGeneralFilters,
      selectedGeneralFiltersApi,
      selectedGeneralFilterGroupsCount,
      selectedGeneralFiltersCount,
      translatedGeneralFilterOptions,
      generalFilterOptions: data?.filters ?? null,
    };
  }, [
    result,
    setSelectedGeneralFilters,
    selectedGeneralFilters,
    selectedGeneralFiltersApi,
    selectedGeneralFilterGroupsCount,
    selectedGeneralFiltersCount,
    translatedGeneralFilterOptions,
    selectedGeneralFiltersWithReactKey,
    data,
  ]);
};
